import { useContext, useRef } from "react";
import {
    AbortError,
    createFailure,
    ensure,
    FailureSeverity,
    validationFailed,
    validationSuccessful
} from "sprint-common-web";

import { TvSeriesReference } from "../../../../api/GetTvSeriesBySimilarTitleResponse";
import { UpdateTvSeriesDetailsRequest } from "../../../../api/UpdateTvSeriesDetailsRequest";
import { useAssetsManageTvSeriesApi } from "../../../../api/useAssetsManageTvSeriesApi";
import { TvSeriesPageContext } from "../../context/TvSeriesPageContext";
import { UpdatedTvSeriesData } from "./EditTvSeriesInfoForm";
import { EditTvSeriesInfoFormInputs } from "./EditTvSeriesInfoFormInputs";

export const useEditTvSeriesInfoForm = () => {
    const { tvSeries } = useContext(TvSeriesPageContext);
    ensure(tvSeries);

    const defaultInputs: EditTvSeriesInfoFormInputs = {
        title: tvSeries.title
    };

    const abortController = useRef<AbortController>(new AbortController());

    const { updateTvSeriesDetails, getTvSeriesBySimilarTitle } = useAssetsManageTvSeriesApi();

    const updateTvSeries = async (inputs: EditTvSeriesInfoFormInputs): Promise<UpdatedTvSeriesData> => {
        return updateTvSeriesDetails(tvSeries.id, createRequest(inputs)).then(() => getUpdatedTvSeriesData(inputs));
    };

    const getUpdatedTvSeriesData = (inputs: EditTvSeriesInfoFormInputs): UpdatedTvSeriesData => {
        ensure(inputs.title);

        return {
            title: inputs.title
        };
    };

    const getTvSeriesWithSimilarTitle = async (title: string): Promise<TvSeriesReference[]> => {
        if (title) {
            const response = await getTvSeriesBySimilarTitle(
                { title, id: tvSeries.id },
                abortController.current.signal
            );

            return response.items;
        }

        return [];
    };

    const validateTitle = (input: string | undefined) => {
        abortController.current.abort();
        abortController.current = new AbortController();

        if (!input) {
            return validationSuccessful();
        }

        return getTvSeriesWithSimilarTitle(input)
            .then((tvSeriesList: TvSeriesReference[]) => {
                if (!tvSeriesList || tvSeriesList.length < 1) return validationSuccessful();

                const warningMessage = `Similar titles: ${tvSeriesList.map((item: TvSeriesReference) => item.title)}`;
                const failure = createFailure(warningMessage, "tvSeries.SimilarTitle", FailureSeverity.Warning);

                return validationFailed([failure]);
            })
            .catch((error: Error) => {
                if (error instanceof AbortError) {
                    return validationSuccessful();
                }

                throw error;
            });
    };

    return {
        defaultInputs,
        updateTvSeries,
        validateTitle
    };
};

const createRequest = (inputs: EditTvSeriesInfoFormInputs): UpdateTvSeriesDetailsRequest => {
    return { title: inputs.title };
};
